import { useState } from "react";
import { api } from "../api.ts";
import { S, useApp, loadBilling, toast, type Billing } from "../store.ts";
import { Icon } from "../ui.tsx";

const money = (b: Billing, market: "IN" | "AE", amount: number) => {
  const p = b.pricing[market]; const n = amount / 100;
  return p.symbol + n.toLocaleString(p.locale, { maximumFractionDigits: n % 1 ? 2 : 0 });
};
const day = (t: number) => new Date(t).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

let rzpLoading: Promise<void> | null = null;
function loadCheckout(): Promise<void> {
  if ((window as any).Razorpay) return Promise.resolve();
  return rzpLoading ||= new Promise((ok, bad) => { const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js"; s.onload = () => ok(); s.onerror = () => { rzpLoading = null; bad(new Error("Couldn't load Razorpay")); }; document.head.appendChild(s); });
}

/** Plan status, prices (INR/AED), Pay once vs Auto-renew, and Razorpay checkout. */
export function Plans() {
  const s = useApp(); const b = s.billing;
  const [mode, setMode] = useState<"once" | "auto">("once"); const [busy, setBusy] = useState<string>(""); const [confirmCancel, setConfirmCancel] = useState(false);
  if (!b) return <p className="note">Loading plans...</p>;
  const m = b.market, mp = b.pricing[m];
  const setMarket = async (x: "IN" | "AE") => { if (x === m) return; await api("/billing/market", { body: { market: x } }).catch(() => {}); await loadBilling(); };
  const buy = async (plan: "m1" | "m3") => {
    setBusy(plan);
    try {
      await loadCheckout();
      const auto = mode === "auto";
      const r = await api(auto ? "/billing/subscribe" : "/billing/order", { body: { plan } });
      await new Promise<void>((done) => {
        const rz = new (window as any).Razorpay({
          key: r.keyId, name: "Numi by LoopSkills", description: r.description, prefill: { email: r.email }, theme: { color: "#F5B400" },
          ...(auto ? { subscription_id: r.subscriptionId } : { order_id: r.orderId, amount: r.amount, currency: r.currency }),
          handler: async (resp: any) => {
            try {
              if (auto) await api("/billing/verify-sub", { body: { subscriptionId: resp.razorpay_subscription_id, paymentId: resp.razorpay_payment_id, signature: resp.razorpay_signature, plan } });
              else await api("/billing/verify-order", { body: { orderId: resp.razorpay_order_id, paymentId: resp.razorpay_payment_id, signature: resp.razorpay_signature } });
              await loadBilling(); toast("Payment done. Thank you.");
            } catch (e: any) { toast(e.message); }
            done();
          },
          modal: { ondismiss: () => done() },
        });
        rz.on?.("payment.failed", (e: any) => toast(e?.error?.description || "Payment didn't go through. Please try again."));
        rz.open();
      });
    } catch (e: any) { toast(e.message || "Payment couldn't start."); }
    setBusy("");
  };
  const cancel = async () => { if (!confirmCancel) { setConfirmCancel(true); return; } try { await api("/billing/cancel", { body: {} }); await loadBilling(); toast("Auto-renew is off. Numi stays open until your paid time ends."); } catch (e: any) { toast(e.message); } setConfirmCancel(false); };
  const daysLeft = Math.max(0, Math.ceil((b.trialEndsAt - Date.now()) / 864e5));
  const trialExpired = b.status === "trial" && daysLeft === 0;
  const m3 = mp.plans.m3, m1 = mp.plans.m1; const save = m3.list ? m3.list - m3.amount : 0;
  return <div className="plans">
    <div className={`pstatus ${b.status}`}>
      {b.status === "free" && <><b>Team access</b><span>This account has free access.</span></>}
      {b.status === "trial" && !trialExpired && <><b>Free trial: {daysLeft} day{daysLeft === 1 ? "" : "s"} left</b><span>Ends {day(b.trialEndsAt)}. Choose a plan any time; your trial days aren't lost.</span></>}
      {b.status === "active" && <><b>{b.autoRenew ? "Subscribed, renews automatically" : "Active"}</b><span>{b.autoRenew ? `Next renewal around ${day(b.paidUntil!)}.` : `Paid until ${day(b.paidUntil!)}.`}{b.subStatus === "cancelling" ? " Auto-renew is off." : ""}</span>
        {b.autoRenew && <button className="linkbtn" onClick={cancel}>{confirmCancel ? "Tap again to turn off auto-renew" : "Turn off auto-renew"}</button>}</>}
      {(b.status === "expired" || trialExpired) && <><b>Your free week has ended</b><span>Choose a plan to keep learning with Numi. Progress and stars are saved.</span></>}
    </div>
    {b.status !== "free" && <>
      <div className="seg" role="radiogroup" aria-label="Country">
        <button role="radio" aria-checked={m === "IN"} className={m === "IN" ? "on" : ""} onClick={() => setMarket("IN")}>India (₹)</button>
        <button role="radio" aria-checked={m === "AE"} className={m === "AE" ? "on" : ""} onClick={() => setMarket("AE")}>UAE (AED)</button>
      </div>
      <div className="seg" role="radiogroup" aria-label="How to pay">
        <button role="radio" aria-checked={mode === "once"} className={mode === "once" ? "on" : ""} onClick={() => setMode("once")}>Pay once</button>
        <button role="radio" aria-checked={mode === "auto"} className={mode === "auto" ? "on" : ""} onClick={() => setMode("auto")}>Auto-renew</button>
      </div>
      <p className="note">{mode === "once" ? "Pay for 1 or 3 months. No auto-debit; we'll remind you before it ends." : "Renews automatically at the same price. Turn it off any time here."}</p>
      <div className="pcards">
        <div className="pcard">
          <span className="pname">1 month</span>
          <span className="pprice">{money(b, m, m1.amount)}</span>
          <span className="psub">{mode === "auto" ? "every month" : "for 30 days"}</span>
          <button className="btn big" disabled={!b.payReady || !!busy} onClick={() => buy("m1")}>{busy === "m1" ? "Opening..." : "Choose"}</button>
        </div>
        <div className="pcard best">
          {save > 0 && <span className="pbadge">Best value · Save {money(b, m, save)}</span>}
          <span className="pname">3 months</span>
          <span className="pprice">{m3.list ? <s>{money(b, m, m3.list)}</s> : null} {money(b, m, m3.amount)}</span>
          <span className="psub">{money(b, m, Math.round(m3.amount / 300) * 100)} a month · {mode === "auto" ? "every 3 months" : "for 90 days"}</span>
          <button className="btn yellow big" disabled={!b.payReady || !!busy} onClick={() => buy("m3")}>{busy === "m3" ? "Opening..." : "Choose"}</button>
        </div>
      </div>
      <p className="note">By paying you agree to our <a href="/terms" target="_blank" rel="noopener">Terms</a> and <a href="/refunds" target="_blank" rel="noopener">Refund Policy</a>. Payments are non-refundable; you can cancel auto-renew any time.</p>
      {!b.payReady && <p className="note"><b>Payments are being switched on.</b> Plans will open here soon.</p>}
      <p className="note"><Icon name="tick" /> Secure payment by Razorpay: UPI, cards and net banking in India; cards in the UAE. One plan covers all your children.</p>
    </>}
  </div>;
}
